import {useCallback, useMemo, useState} from 'react';
import {FieldPath, FieldValues, UseFormReturn} from 'react-hook-form';

import {TextInputRef} from '../../../components/ui-controls/form/TextInput';
import {checkPasswordRequirements} from '../utils/passwordValidation';

const FOCUS_DELAY_MS = 100;
const DEFAULT_WEAK_PASSWORD_MESSAGE = 'This password is too weak or may be compromised';

export interface WeakPasswordError {
  status: string;
  message: string;
}

export interface UsePasswordInputConfig<TFormValues extends FieldValues> {
  form: UseFormReturn<TFormValues>;
  fieldName?: FieldPath<TFormValues>;
  confirmFieldName?: FieldPath<TFormValues>;
  bypassFieldName?: FieldPath<TFormValues>;
  onWeakPasswordDetected?: () => void;
  autoFocusOnError?: boolean;
  passwordRef?: React.RefObject<TextInputRef>;
  confirmPasswordRef?: React.RefObject<TextInputRef>;
}

export function usePasswordInput<TFormValues extends FieldValues>({
  form,
  fieldName = 'password' as FieldPath<TFormValues>,
  confirmFieldName,
  bypassFieldName = 'passwordValidationBypass' as FieldPath<TFormValues>,
  onWeakPasswordDetected,
  autoFocusOnError = true,
  passwordRef,
  confirmPasswordRef,
}: UsePasswordInputConfig<TFormValues>) {
  // State
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [isPasswordFocused, setPasswordFocused] = useState(false);
  const [showWeakPasswordCheckbox, setShowWeakPasswordCheckbox] = useState(false);

  // Actions
  const togglePasswordVisibility = useCallback(() => {
    setPasswordVisible(prev => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setConfirmPasswordVisible(prev => !prev);
  }, []);

  const reset = useCallback(() => {
    setPasswordVisible(false);
    setConfirmPasswordVisible(false);
    setPasswordFocused(false);
    setShowWeakPasswordCheckbox(false);
  }, []);

  const handleWeakPasswordError = useCallback(
    (error: WeakPasswordError) => {
      if (error.status !== 'PASSWORD_TOO_WEAK') {
        return;
      }

      setShowWeakPasswordCheckbox(true);
      form.setValue(bypassFieldName, false as TFormValues[typeof bypassFieldName]);
      form.setError(fieldName, {
        type: 'manual',
        message: error.message || DEFAULT_WEAK_PASSWORD_MESSAGE,
      });

      onWeakPasswordDetected?.();

      if (autoFocusOnError && passwordRef?.current) {
        setTimeout(() => {
          passwordRef.current?.focus();
        }, FOCUS_DELAY_MS);
      }
    },
    [form, fieldName, bypassFieldName, onWeakPasswordDetected, autoFocusOnError, passwordRef],
  );

  const resetWeakPasswordState = useCallback(() => {
    setShowWeakPasswordCheckbox(false);
  }, []);

  // Watch password value from form
  const passwordValue = (form.watch(fieldName) as string) || '';
  const bypassValue = form.watch(bypassFieldName) as boolean;

  // Check if all requirements are met
  const allRequirementsMet = useMemo(() => {
    const reqs = checkPasswordRequirements(passwordValue);
    return reqs.every(req => req.isMet);
  }, [passwordValue]);

  // Extract form state values for proper reactivity
  const {isTouched} = form.getFieldState(fieldName);
  const fieldError = form.formState.errors[fieldName];
  const {isSubmitted} = form.formState;

  // Pre-wired props
  const indicatorProps = useMemo(
    () => ({
      password: passwordValue,
      show: isTouched || isPasswordFocused || !!passwordValue || !!fieldError || isSubmitted,
    }),
    [passwordValue, isPasswordFocused, isTouched, fieldError, isSubmitted],
  );

  const toggleButtonProps = useMemo(
    () => ({
      isPasswordVisible,
      onPress: togglePasswordVisibility,
    }),
    [isPasswordVisible, togglePasswordVisibility],
  );

  const confirmToggleButtonProps = useMemo(
    () => ({
      isPasswordVisible: isConfirmPasswordVisible,
      onPress: toggleConfirmPasswordVisibility,
    }),
    [isConfirmPasswordVisible, toggleConfirmPasswordVisibility],
  );

  const bypassCheckboxProps = useMemo(
    () => ({
      visible: showWeakPasswordCheckbox || bypassValue,
      checked: bypassValue || false,
      onChange: (checked: boolean) =>
        form.setValue(bypassFieldName, checked as TFormValues[typeof bypassFieldName]),
    }),
    [form, bypassFieldName, showWeakPasswordCheckbox, bypassValue],
  );

  // Field handlers for Controller render functions
  const passwordFieldHandlers = useMemo(
    () => ({
      onFocus: () => setPasswordFocused(true),
      onBlur: () => setPasswordFocused(false),
      onChangeText: (_text: string) => {
        // Use getFieldState to read current error state (avoids stale closure)
        const {error} = form.getFieldState(fieldName);
        if (error && error.type === 'manual') {
          form.clearErrors(fieldName);
        }

        // Reset weak password state when user changes password
        if (showWeakPasswordCheckbox) {
          setShowWeakPasswordCheckbox(false);
        }

        // Trigger confirm password validation if it exists and has been touched
        if (confirmFieldName) {
          const {isTouched: isConfirmTouched} = form.getFieldState(confirmFieldName);
          if (isConfirmTouched) {
            form.trigger(confirmFieldName);
          }
        }
      },
      onSubmitEditing: () => {
        // Auto-advance to confirm password field if ref provided
        if (confirmPasswordRef?.current) {
          confirmPasswordRef.current.focus();
        }
      },
    }),
    [form, fieldName, confirmFieldName, confirmPasswordRef, showWeakPasswordCheckbox],
  );

  const confirmFieldHandlers = useMemo(
    () => ({
      onChangeText: (_text: string) => {
        // Trigger validation when user types in confirm field
        // Use getFieldState to read current touched state (avoids stale closure)
        if (confirmFieldName) {
          const {isTouched: isPasswordTouched} = form.getFieldState(fieldName);
          if (isPasswordTouched) {
            form.trigger(confirmFieldName);
          }
        }
      },
    }),
    [form, fieldName, confirmFieldName],
  );

  return {
    // State
    isPasswordVisible,
    isConfirmPasswordVisible,
    isPasswordFocused,
    showWeakPasswordCheckbox,

    // Derived values
    passwordValue,
    allRequirementsMet,

    // Pre-wired props
    indicatorProps,
    toggleButtonProps,
    confirmToggleButtonProps,
    bypassCheckboxProps,

    // Field handlers
    passwordFieldHandlers,
    confirmFieldHandlers,

    // Actions
    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
    setPasswordFocused,
    reset,
    handleWeakPasswordError,
    resetWeakPasswordState,
  };
}
